import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, User, Building2, Layers, FileText, Shield, BookOpen, Monitor, CheckCircle2 } from 'lucide-react';

const services = [
  {
    slug: 'individual-tax',
    icon: User, title: 'Individual Tax Return',
    desc: 'Individual Tax returns, even with all the tools provided, can be confusing sometimes. Especially what to claim and how to claim it legally to get the best possible outcome. We can help you to get the best legal tax return for you.',
    points: ['Salary & wage earners','Investment income','Rental properties','Work-related deductions','Sole traders'],
    image: 'https://images.unsplash.com/photo-1586486855514-8c633cc6fd38?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'business-tax',
    icon: Building2, title: 'Business Tax Return',
    desc: 'Running a business itself is a tiresome task. Worrying about tax complications won\'t make life easier. Please let us take the stress out of your business; let us do Tax planning for you to get more time for your business, family, and things you love most.',
    points: ['Companies & trusts','Partnerships','Tax planning','Deduction maximisation','ATO correspondence'],
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'business-structuring',
    icon: Layers, title: 'Business Structuring',
    desc: 'Setting your business structure right is of utmost importance. It helps you in asset protection, saving Tax bills, and Tax planning. Whatever your structure — sole trader, company or Trust — we guide you according to your personalised requirements.',
    points: ['Sole trader setup','Company formation','Trust structures','Asset protection','ABN registration'],
    image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'bas',
    icon: FileText, title: 'Business Activity Statement (BAS)',
    desc: 'GST, when claimed appropriately, can save lots of dollars. We help you plan your GST expenses and lodge them promptly. Contact us for all your BAS lodgement requirements — UBER, Didi, Olla all welcome.',
    points: ['Monthly & quarterly BAS','GST planning','Ride-share BAS (Uber/Didi/Olla)','PAYG withholding','IAS lodgements'],
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'asic',
    icon: Shield, title: 'ASIC Lodgments',
    desc: 'We are a registered ASIC agent practice. Please contact us for all your ASIC compliance requirements. We handle all ASIC lodgements efficiently and accurately on your behalf.',
    points: ['Annual company statements','Director changes','Registered office updates','Share transfers','Company deregistration'],
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'bookkeeping',
    icon: BookOpen, title: 'Bookkeeping',
    desc: 'The day-to-day running of the business can be overwhelming, leaving a huge pile of invoices to be booked. Whether it is a one-off requirement or an ongoing condition, contact us for all your Bookkeeping requirements.',
    points: ['Accounts payable & receivable','Bank reconciliations','Payroll processing','Monthly reporting','BAS preparation'],
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&h=650&fit=crop&q=80',
  },
  {
    slug: 'software-training',
    icon: Monitor, title: 'Software Training',
    desc: 'Get all your cloud-based accounting software sorted — whether it is MYOB, Quickbooks or Xero. Expert advice from startup phase depending upon your needs.',
    points: ['Xero setup & training','MYOB configuration','Quickbooks onboarding','Cloud migration','Ongoing support'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=650&fit=crop&q=80',
  },
];

const startupSteps = [
  'We hear your thoughts and plans regarding your business, and we will offer you another point of view to extend the probability of success.',
  'You\'ll get a recommendation on the most appropriate structure — company, trust or sole trader — with advantages and disadvantages explained.',
  'You\'ll get your ABN registered together with tax registrations like GST, PAYG, TFN and Withholding.',
  'We guide you to obtain appropriate Accounting Software and get your new business name registered.',
  'We offer unlimited access to us, so you can make better decisions at every step.',
  'We help you get more time in your business and less stress in running it.',
];

export default function Services() {
  const { slug } = useParams();

  useEffect(() => {
    if (!slug) return;
    const t = setTimeout(() => {
      const el = document.getElementById(slug);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 80);
    return () => clearTimeout(t);
  }, [slug]);

  return (
    <div className="page-enter">
      <style>{`
        .page-hero { background: var(--charcoal-dark); padding: 72px 0; position: relative; overflow: hidden; }
        .page-hero-img { position:absolute; inset:0; background-image:url('https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1600&h=500&fit=crop&q=80'); background-size:cover; background-position:center; filter:saturate(1.05) brightness(0.9); }
        .page-hero-overlay { position:absolute; inset:0; background: linear-gradient(90deg, rgba(26,26,24,0.94) 0%, rgba(26,26,24,0.8) 35%, rgba(26,26,24,0.45) 68%, rgba(26,26,24,0.2) 100%); }
        .page-hero::before { content:''; position:absolute; right:0; top:0; bottom:0; width:40%; background: radial-gradient(circle at 80% 50%, rgba(201,168,76,0.14),transparent 70%); pointer-events:none; z-index:1; }
        .ph-breadcrumb { font-size:12px; color:rgba(255,255,255,0.4); margin-bottom:12px; }
        .ph-breadcrumb a { color:var(--gold); }
        .ph-title { font-family:var(--font-display); font-size:clamp(32px,4vw,52px); font-weight:700; color:white; line-height:1.15; margin-bottom:12px; }
        .ph-title em { font-style:italic; color:var(--gold); }
        .ph-sub { font-size:16px; font-weight:300; color:rgba(255,255,255,0.6); max-width:580px; line-height:1.75; }
        .svc-full { padding: 96px 0; background: var(--white); }
        .svc-full-grid { display:flex; flex-direction:column; gap:26px; }
        .sfc {
          display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center;
          background:var(--white); padding:28px; transition:all 0.3s ease; position:relative; overflow:hidden;
          border:1px solid var(--border); border-radius:var(--radius-lg); box-shadow:var(--shadow-sm);
          scroll-margin-top:96px;
        }
        .sfc:hover { box-shadow:var(--shadow-lg); transform:translateY(-4px); border-color:var(--gold); }
        .sfc.is-target { border-color:var(--gold); box-shadow:var(--shadow-lg); }
        .sfc-img { order:1; width:100%; height:300px; border-radius:var(--radius); overflow:hidden; }
        .sfc-content { order:2; padding:8px 16px; }
        .sfc.reverse .sfc-img { order:2; }
        .sfc.reverse .sfc-content { order:1; }
        .sfc-img img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.5s ease; }
        .sfc:hover .sfc-img img { transform:scale(1.05); }
        .sfc-icon { width:52px; height:52px; background:var(--gold-bg); border:1px solid rgba(201,168,76,0.25); border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; color:var(--gold); margin-bottom:20px; }
        .sfc-title { font-family:var(--font-display); font-size:24px; font-weight:700; color:var(--text-dark); margin-bottom:12px; }
        .sfc-desc { font-size:14px; line-height:1.8; color:var(--text-body); margin-bottom:20px; }
        .sfc-points { display:flex; flex-direction:column; gap:8px; }
        .sfc-point { display:flex; align-items:center; gap:8px; font-size:13px; color:var(--text-body); }
        .sfc-point svg { color:var(--gold); flex-shrink:0; }
        @media (max-width:800px) {
          .sfc { grid-template-columns:1fr; gap:22px; padding:20px; }
          .sfc-img, .sfc.reverse .sfc-img { order:1; height:220px; }
          .sfc-content, .sfc.reverse .sfc-content { order:2; padding:4px 4px 8px; }
        }
        .startup-band { background:var(--charcoal-dark); padding:88px 0; }
        .startup-grid { display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
        .startup-left {}
        .startup-steps { display:flex; flex-direction:column; gap:16px; margin-top:32px; }
        .ss { display:flex; gap:16px; padding:20px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:var(--radius-sm); transition:border-color 0.2s; }
        .ss:hover { border-color:var(--gold); }
        .ss-num { width:32px; height:32px; border-radius:50%; background:var(--gold); display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; color:var(--charcoal-dark); flex-shrink:0; }
        .ss-text { font-size:14px; line-height:1.7; color:rgba(255,255,255,0.65); }
        .startup-right { position:sticky; top:100px; display:flex; flex-direction:column; gap:20px; }
        .sr-box { background:var(--gold); padding:44px 40px; border-radius:var(--radius); }
        .srb-eyebrow { font-size:11px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:rgba(42,42,42,0.6); margin-bottom:12px; }
        .srb-title { font-family:var(--font-display); font-size:28px; font-weight:700; color:var(--charcoal-dark); margin-bottom:16px; }
        .srb-text { font-size:15px; line-height:1.8; color:rgba(42,42,42,0.75); margin-bottom:24px; }
        .sr-regs { background:rgba(255,255,255,0.15); padding:24px; border-radius:var(--radius-sm); }
        .sr-regs-title { font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:var(--charcoal-dark); margin-bottom:14px; }
        .sr-reg { display:flex; align-items:center; gap:10px; padding:9px 0; border-bottom:1px solid rgba(42,42,42,0.1); font-size:14px; color:var(--charcoal-dark); }
        .sr-reg:last-child { border-bottom:none; }
        .sr-dot { width:6px; height:6px; background:var(--charcoal-dark); border-radius:50%; flex-shrink:0; }
        @media (max-width:900px) { .startup-grid{grid-template-columns:1fr; gap:40px;} .startup-right{position:static;} }
      `}</style>

      <div className="page-hero">
        <div className="page-hero-img" />
        <div className="page-hero-overlay" />
        <div className="container" style={{position:'relative',zIndex:2}}>
          <div className="ph-breadcrumb"><Link to="/">Home</Link> / Services</div>
          <h1 className="ph-title">What We <em>Do</em></h1>
          <p className="ph-sub">We are professionally qualified & experienced people. With over eight years in Accounting and Taxation, we ensure the best possible outcome for all your obligations.</p>
        </div>
      </div>

      <section className="svc-full">
        <div className="container">
          <div style={{textAlign:'center',marginBottom:'60px'}}>
            <span className="section-eyebrow">Our Services</span>
            <h2 className="section-title">We Have Helped Many With<br /><em>Our Expertise</em></h2>
          </div>
          <div className="svc-full-grid">
            {services.map(({ slug: svcSlug, icon: Icon, title, desc, points, image }, i) => (
              <div
                key={title} id={svcSlug}
                className={`sfc ${i % 2 === 1 ? 'reverse' : ''} ${slug === svcSlug ? 'is-target' : ''}`}
              >
                <div className="sfc-img"><img src={image} alt={title} loading="lazy" /></div>
                <div className="sfc-content">
                  <div className="sfc-icon"><Icon size={22} /></div>
                  <div className="sfc-title">{title}</div>
                  <div className="sfc-desc">{desc}</div>
                  <div className="sfc-points">
                    {points.map(p => <div key={p} className="sfc-point"><CheckCircle2 size={14} />{p}</div>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="startup-band">
        <div className="container">
          <div className="startup-grid">
            <div className="startup-left">
              <span style={{fontSize:'11px',fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'var(--gold)',display:'block',marginBottom:'12px'}}>Business Startup</span>
              <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(28px,3.5vw,42px)',fontWeight:700,color:'white',lineHeight:1.2,marginBottom:'16px'}}>Starting a Business?</h2>
              <p style={{fontSize:'15px',lineHeight:1.8,color:'rgba(255,255,255,0.6)',marginBottom:'8px'}}>You know what business you will create and how to perform well in the industry. You may need help structuring it to suit your individual needs.</p>
              <p style={{fontSize:'13px',fontWeight:600,letterSpacing:'1px',textTransform:'uppercase',color:'var(--gold)',margin:'24px 0 0'}}>Here's how it works:</p>
              <div className="startup-steps">
                {startupSteps.map((step, i) => (
                  <div key={i} className="ss">
                    <div className="ss-num">{i + 1}</div>
                    <div className="ss-text">{step}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="startup-right">
              <div className="sr-box">
                <div className="srb-eyebrow">Get Started Today</div>
                <div className="srb-title">Ready to Start Your Business?</div>
                <p className="srb-text">We make business setup simple. From ABN registration to choosing the right structure and setting up your accounting software — Trinity is with you from day one.</p>
                <Link to="/contact" className="btn-dark">
                  Book a Free Consultation <ArrowRight size={14} />
                </Link>
              </div>
              <div className="sr-regs">
                <div className="sr-regs-title">We Register</div>
                {['ABN','GST','PAYG','TFN & Withholding','Business Name','Accounting Software Setup'].map(r => (
                  <div key={r} className="sr-reg"><div className="sr-dot" />{r}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
